import { ConfirmDialog } from "@/components/ConfirmDialog";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import EditNoteIcon from "@mui/icons-material/EditNote";
import HomeIcon from "@mui/icons-material/Home";
import PublicIcon from "@mui/icons-material/Public";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { furnitureKeys } from "../inventory/queryKeys";
import { roomKeys, roomsKeys } from "../staging-view/queryKeys";
import { stagingsApi } from "./api";
import { stagingsKeys } from "./queryKeys";
import { StagingInfoDialog } from "./StagingInfoDialog";
import { STAGING_STATUS_OPTIONS, StagingStatusFilter } from "./StagingStatusFilter";

const PAGE_SIZES = [10, 30, 100];

// Long enough that an address is typed, not transmitted keystroke by keystroke;
// short enough that the table still feels like it is reacting to the field.
const SEARCH_DEBOUNCE_MS = 350;

export function StagingsPanel() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [info, setInfo] = useState<number | null>(null);
  const [selectedStagingForMove, setSelectedStagingForMove] = useState(0);
  const [confirmMoveToStorage, setConfirmMoveToStorage] = useState(false);
  const [rawSearch, setRawSearch] = useState("");
  const [search, setSearch] = useState("");
  const [statuses, setStatuses] = useState<number[]>([]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(rawSearch), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [rawSearch]);

  // A narrower result set has fewer pages, so the page the user was on may no
  // longer exist. Going back to the first one keeps the table from looking empty.
  useEffect(() => {
    setPage(1);
  }, [search, statuses]);

  const skip = (page - 1) * pageSize;
  const query = useQuery({
    queryKey: stagingsKeys.summary({ skip, limit: pageSize, search, statuses }),
    queryFn: () => stagingsApi.summary({ skip, limit: pageSize, search, statuses }),
    // Keeps the previous page on screen while a debounced search resolves,
    // instead of blanking the table on every settled keystroke.
    placeholderData: (prev) => prev,
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: number }) =>
      stagingsApi.patchStatus(id, status),
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: stagingsKeys.summaries() });
      void qc.invalidateQueries({ queryKey: stagingsKeys.detail(id) });
    },
  });

  const rows = query.data ?? [];
  const hasMore = rows.length === pageSize;

  const moveItemsToStorageFn = async (staging: number): Promise<number> => {
    await stagingsApi.moveItemsToStorage(staging);
    return staging;
  };
  const moveItemsToStorage = useMutation({
    mutationFn: moveItemsToStorageFn,
    onSuccess: (staging) => {
      // Staging-wide move can touch any room, so refresh every room's detail.
      void qc.invalidateQueries({ queryKey: roomKeys.all });
      void qc.invalidateQueries({ queryKey: roomsKeys.list(staging) });
      void qc.invalidateQueries({ queryKey: furnitureKeys.lists() });
      void qc.invalidateQueries({ queryKey: stagingsKeys.summaries() });
      setConfirmMoveToStorage(false);
    },
  });
  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: { xs: 1, sm: 2 },
          mb: 1,
        }}
      >
        <Typography variant="h6" sx={{ flex: 1, minWidth: 140 }}>
          Properties
        </Typography>
        <TextField
          size="small"
          placeholder="Search address"
          value={rawSearch}
          onChange={(e) => setRawSearch(e.target.value)}
          sx={{ flex: { xs: 1, sm: "0 0 auto" }, minWidth: 180 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: rawSearch
                ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setRawSearch("")}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
                : null,
            },
          }}
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <StagingStatusFilter statuses={statuses} onChange={setStatuses} />
      </Box>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: { xs: 480, sm: 0 } }}>
          <TableHead>
            <TableRow>
              <TableCell>ADDRESS</TableCell>
              <TableCell>STATUS</TableCell>
              <TableCell align="right" sx={{ display: { xs: "none", sm: "table-cell" } }}>
                INVENTORY LOCATIONS
              </TableCell>
              <TableCell align="right">ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {query.isLoading && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            )}
            {query.isError && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="error">Failed to load stagings</Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell
                  onClick={() => void nav(`/staging/${row.id}/room-builder`)}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "primary.main",
                      fontWeight: 500,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {row.address}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Select
                    size="small"
                    variant="standard"
                    value={row.status}
                    onChange={(e) =>
                      updateStatus.mutate({ id: row.id, status: Number(e.target.value) })}
                    onClick={(e) => e.stopPropagation()}
                    disableUnderline
                    sx={{ minWidth: 110 }}
                  >
                    {STAGING_STATUS_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>

                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<WarehouseIcon />}
                    disabled={moveItemsToStorage.isPending}
                    onClick={() => {
                      setSelectedStagingForMove(row.id);
                      setConfirmMoveToStorage(true);
                    }}
                    sx={{ textTransform: "none" }}
                  >
                    Move all to storage
                  </Button>
                </TableCell>
                <TableCell align="right" sx={{ display: { xs: "none", sm: "table-cell" } }}>
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Tooltip title="Locker">
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        sx={{ minWidth: 50 }}
                      >
                        <WarehouseIcon fontSize="small" color="action" />
                        <span>{row.lockerItems}</span>
                      </Stack>
                    </Tooltip>
                    <Tooltip title="On site">
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        sx={{ minWidth: 50 }}
                      >
                        <HomeIcon fontSize="small" color="action" />
                        <span>{row.onSiteItems}</span>
                      </Stack>
                    </Tooltip>
                    <Tooltip title="At other houses">
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        sx={{ minWidth: 50 }}
                      >
                        <PublicIcon fontSize="small" color="action" />
                        <span>{row.atOtherLocation}</span>
                      </Stack>
                    </Tooltip>
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => void nav(`/administration/stagings/${row.id}?returnTo=/`)}
                    >
                      <EditNoteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Info">
                    <IconButton size="small" color="primary" onClick={() => setInfo(row.id)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!query.isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="text.secondary">
                    {search || statuses.length > 0
                      ? "No properties match these filters"
                      : "No stagings"}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: { xs: 1, sm: 2 },
          mt: 2,
        }}
      >
        <Typography variant="body2">Page:</Typography>
        <Typography variant="body2">{page}</Typography>
        <Typography variant="body2">Rows per page:</Typography>
        <Select
          size="small"
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(1);
          }}
          variant="standard"
        >
          {PAGE_SIZES.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </Select>
        <Typography variant="body2" sx={{ mx: 1 }}>
          {rows.length === 0 ? "0" : `${skip + 1} - ${skip + rows.length}`}
        </Typography>
        <IconButton
          size="small"
          disabled={page === 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton size="small" disabled={!hasMore} onClick={() => setPage((p) => p + 1)}>
          <ChevronRightIcon />
        </IconButton>
      </Box>
      <StagingInfoDialog stagingId={info} onClose={() => setInfo(null)} />

      <ConfirmDialog
        open={confirmMoveToStorage}
        title="Move all to storage"
        message="Move every furniture item currently placed in any room of this staging back to its storage location? This affects all rooms, not just the one you're viewing."
        confirmLabel="Move to storage"
        confirmColor="primary"
        onClose={() => setConfirmMoveToStorage(false)}
        isPending={moveItemsToStorage.isPending}
        onConfirm={() => moveItemsToStorage.mutate(selectedStagingForMove)}
      />
    </Paper>
  );
}
